import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { CloseIcon, DropDownArrow } from '@yme/assets/icons';
import { BottomSheet, Header } from '@yme/atoms/bottom-sheet';
import { RadioButton } from '@yme/atoms/radio-button';
import { SVGIcon } from '@yme/atoms/svg-icon';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { NoResultComponent } from '@yme/molecules/no-results-component';
import { colorCoreGray1000Base } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { MOBILE_DROPDOWN_SLIDER_CLASS } from '../mobile-dropdown-slider/constants';
import { NO_FOUND_ITEMS, PLACEHOLDER } from './constants';
import { getActiveFromString, getActiveItemFromItems } from './helpers';
import { ArrowStyled, DropdownStyled, ErrorMessage, LabelStyled, ListItemStyled, ListStyled, ListStyledMobile, NoResultsStyled, SVGIconStyled, TogglerStyled, ListItemRadioStyled, } from './styled';
function DropdownComponent({ isRightToLeft = false, errorMessage, items = [], isDisabled = false, activeItem, hasAutocomplete, inputPlaceholder = PLACEHOLDER, isNoResultComponentShowed = false, isFlexible, labelText, title, isRequired = false, useForMobile = false, isInheritBackground = false, leftSideIconType, hasRadioButton = false, contents = false, dataTestId = 'default', paddingValue, onChange, focusHandler, blurHandler, onScroll, className, noResultTitle = '', noResultDescription = '', renderContent, isShowIconThenListOpened = false, }) {
    const [isListOpen, setIsListOpen] = useState(false);
    const [inputText, setInputText] = useState('');
    const [hasScroll, setHasScroll] = useState(false);
    const [isUpwardDirection, setIsUpwardDirection] = useState(false);
    const { isInfiniteDesktopView: isDesktop } = useWindowSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onOptionClicked = ({ value, label }) => {
        if (onChange && value !== activeItem) {
            onChange(value, label);
        }
        setIsListOpen(false);
    };
    const ref = useRef(null);
    const refMobile = useRef(null);
    const inputRef = useRef(null);
    const arrowRef = useRef(null);
    const getClosestParentContainer = (element) => {
        if (!element)
            return null;
        const container = element?.closest(`.${MOBILE_DROPDOWN_SLIDER_CLASS}`);
        return container ? container : null;
    };
    useEffect(() => {
        if (!ref.current) {
            return;
        }
        const bottomMargin = 54;
        const topPadding = 24;
        const minMaxHeight = 160;
        const nodeParent = ref.current.parentElement;
        const parentWithHiddenOverflow = getClosestParentContainer(nodeParent);
        if (!parentWithHiddenOverflow || !nodeParent) {
            return;
        }
        const parentCoords = parentWithHiddenOverflow.getBoundingClientRect();
        const nodeContainerCoords = nodeParent.getBoundingClientRect();
        const effectiveHeight = nodeContainerCoords.bottom - parentCoords.top;
        const maxHeight = effectiveHeight - bottomMargin - topPadding;
        if (maxHeight < minMaxHeight) {
            return;
        }
        ref.current.style.maxHeight = `${maxHeight}px`;
    }, []);
    useEffect(() => {
        if (useForMobile && !isDesktop) {
            return;
        }
        const handleClickOutside = (event) => {
            if (!ref.current?.contains(event.target) &&
                !inputRef.current?.contains(event.target) &&
                !arrowRef.current?.contains(event.target)) {
                if (isListOpen && blurHandler) {
                    blurHandler();
                }
                setIsListOpen(false);
                setInputText('');
            }
            event.stopPropagation();
        };
        window.addEventListener('mousedown', handleClickOutside);
        // eslint-disable-next-line consistent-return
        return () => {
            window.removeEventListener('mousedown', handleClickOutside);
        };
    }, [blurHandler, isDesktop, isListOpen, ref, useForMobile]);
    const getHasScroll = () => {
        if (ref.current) {
            return ref.current.scrollHeight > ref.current.clientHeight;
        }
        return false;
    };
    useEffect(() => {
        if (isListOpen) {
            inputRef.current?.focus();
            setHasScroll(getHasScroll());
        }
        else {
            setIsUpwardDirection(false);
        }
    }, [isListOpen]);
    const onClickInput = (event) => {
        event.stopPropagation();
        return setIsListOpen(true);
    };
    const onClickToggler = () => {
        const offsetOptions = ref.current?.getBoundingClientRect();
        if (!isListOpen && focusHandler) {
            focusHandler();
        }
        if (!isListOpen && offsetOptions && offsetOptions.bottom > window.innerHeight) {
            setIsUpwardDirection(true);
        }
        setIsListOpen(!isListOpen);
    };
    const chooseIcon = () => (isListOpen && hasAutocomplete ? CloseIcon : DropDownArrow);
    const toggleExpandedDropDownState = () => isListOpen && !isDisabled && !hasAutocomplete;
    const showInputValue = () => {
        const activeItemObj = items.find((item) => item.value === activeItem);
        return activeItemObj?.label;
    };
    const showInputIcon = () => {
        const activeItemObj = items.find((item) => item.value === activeItem);
        return activeItemObj?.icon;
    };
    const ItemsElement = useMemo(() => {
        const itemsList = hasAutocomplete
            ? items.filter(({ label }) => label.toLowerCase().indexOf(inputText.toLowerCase()) > -1)
            : items;
        const renderList = () => {
            if (itemsList.length) {
                return itemsList.map(({ label, value, icon }) => {
                    if (!isDesktop && hasRadioButton) {
                        return (React.createElement(ListItemRadioStyled, { key: `${label}_${value}` },
                            React.createElement(RadioButton, { id: value, name: value, value: value, isChecked: value === activeItem, isRightToLeft: isRightToLeft, label: label, onChange: () => onOptionClicked({
                                    label,
                                    value,
                                }) })));
                    }
                    return (React.createElement(ListItemStyled, { onClick: () => onOptionClicked({
                            label,
                            value,
                        }), key: `${label}_${value}`, isFlexible: isFlexible, isActiveItem: value === activeItem },
                        icon,
                        label));
                });
            }
            if (!itemsList.length && isNoResultComponentShowed) {
                return (React.createElement(FlexWrapper, { padding: "24px", gap: "12px", flexDirection: "column", alignItems: "center" },
                    React.createElement(NoResultComponent, { noResultDescription: noResultDescription, noResultTitle: noResultTitle })));
            }
            return React.createElement(NoResultsStyled, null, NO_FOUND_ITEMS);
        };
        return renderContent ? renderContent() : renderList();
    }, [
        activeItem,
        hasAutocomplete,
        inputText,
        isDesktop,
        isFlexible,
        isNoResultComponentShowed,
        items,
        noResultDescription,
        noResultTitle,
        hasRadioButton,
        isRightToLeft,
        onOptionClicked,
        renderContent,
    ]);
    const renderActiveItem = useCallback(() => isEmpty(items)
        ? getActiveFromString(activeItem, isDisabled, inputPlaceholder, isRightToLeft, contents)
        : getActiveItemFromItems({
            activeItem,
            items,
            isDisabled,
            isRightToLeft,
            inputPlaceholder,
            contents,
        }), [items, activeItem, isDisabled, inputPlaceholder, contents, isRightToLeft]);
    return (React.createElement(DropdownStyled, { isDisabled: isDisabled, "data-testid": `dropdown-${dataTestId}` },
        labelText && (React.createElement(LabelStyled, { isRequired: isRequired, isRightToLeft: isRightToLeft }, labelText)),
        React.createElement(TogglerStyled, { tabIndex: 0, paddingValue: paddingValue, isListOpen: isListOpen, isInheritBackground: isInheritBackground, isDisabled: isDisabled, onClick: onClickToggler, isFlexible: isFlexible, className: className, error: errorMessage },
            leftSideIconType && React.createElement(SVGIcon, { type: leftSideIconType }),
            isListOpen ? (React.createElement(FlexWrapper, { gap: "16px", alignItems: "center" },
                isShowIconThenListOpened && showInputIcon(),
                React.createElement("input", { value: hasAutocomplete ? inputText : showInputValue(), placeholder: inputPlaceholder, type: "text", ref: inputRef, "aria-autocomplete": "list", "aria-expanded": "false", spellCheck: "false", disabled: isListOpen && !hasAutocomplete, onChange: (e) => setInputText(e.target.value), onClick: (e) => onClickInput(e) }))) : (renderActiveItem()),
            React.createElement(ArrowStyled, { ref: arrowRef, isRightToLeft: isRightToLeft },
                React.createElement(SVGIconStyled, { type: chooseIcon(), isExpandedDropDown: toggleExpandedDropDownState(), pathFill: isDisabled ? colorCoreGray1000Base : undefined }))),
        useForMobile && !isDesktop ? (React.createElement(BottomSheet, { open: isListOpen, onDismiss: () => {
                setIsListOpen(false);
            }, header: React.createElement(Header, { isRightToLeft: isRightToLeft, headerLeft: title || labelText }) },
            React.createElement(ListStyledMobile, { ref: refMobile, onScroll: onScroll ? () => onScroll(refMobile) : undefined, isRightToLeft: isRightToLeft, "data-body-scroll-lock-ignore": true }, ItemsElement))) : (React.createElement(ListStyled, { role: "list", "aria-expanded": true, onScroll: onScroll ? () => onScroll(ref) : undefined, ref: ref, isRightToLeft: isRightToLeft, hasScroll: hasScroll, isUpwardDirection: isUpwardDirection, isListOpen: isListOpen }, ItemsElement)),
        errorMessage && React.createElement(ErrorMessage, { error: errorMessage }, errorMessage)));
}
export const Dropdown = memo(DropdownComponent);
Dropdown.displayName = 'Dropdown_Atom';
